import type { NoticeProduct } from "../api/types";
import type {
  ConfirmVerdict,
  ProductConfig,
  ProductSeed,
  ProductSeedInput,
  PurposeRow,
  ScopedPurpose,
} from "../types";
import {
  gatedProductUuids,
  groupPurposesByProduct,
  isMultiProduct,
  noticePurposeRows,
  serverMarksMandatoryProducts,
} from "../core/productMatrix";
import {
  collapseEveryRow,
  confirmVerdict,
  initialCollapsedProducts,
  isFullyConsented,
  recordedBaseline,
  recordedSelectionLookup,
  seedSelection,
  settleProducts,
} from "./productConsent";

export const rowsOf = <P extends ScopedPurpose>(
  config: ProductConfig & { purposes: P[] },
): PurposeRow<P>[] =>
  noticePurposeRows(config.products, config.purposes, config.product_purpose_order);

export const groupsOf = <P extends ScopedPurpose>(
  config: ProductConfig & { purposes: P[] },
) => groupPurposesByProduct(config.products, config.purposes, config.product_purpose_order);

export const seedProductState = <P extends ScopedPurpose>({
  config,
  purposeSelections,
  productPurposeSelections,
  defaultOpenProducts,
  includeFullyConsentedData,
}: ProductSeedInput<P>): ProductSeed<P> => {
  const rows = rowsOf(config);
  const recordedFor = recordedSelectionLookup(purposeSelections, productPurposeSelections);
  const hasRecord = Boolean(purposeSelections || productPurposeSelections);
  const settled = hasRecord
    ? settleProducts(rows, config.products, recordedFor)
    : { consentedProducts: {}, consentedPurposes: {} };

  const seed: ProductSeed<P> = {
    collapsedProducts: initialCollapsedProducts(groupsOf(config), defaultOpenProducts),
    recordedPurposeSelections: recordedBaseline(rows, recordedFor),
    consentedProducts: settled.consentedProducts,
    consentedPurposes: settled.consentedPurposes,
  };
  if (!isMultiProduct(config.products)) return seed;

  const seeded = seedSelection(rows, recordedFor);
  return {
    ...seed,
    collapsedPurposes: collapseEveryRow(rows),
    selectedPurposes: seeded.selectedPurposes,
    selectedDataElements: seeded.selectedDataElements,
    initialDataElementSelections: seeded.selectedDataElements,
    needsConsentPurposes: config.purposes,
    alreadyConsentedPurposes: [],
    isFullyConsentedReviewMode: Boolean(
      includeFullyConsentedData &&
        hasRecord &&
        rows.length > 0 &&
        isFullyConsented(rows, recordedFor),
    ),
  };
};

export const productIsRequired = (
  products: ReadonlyArray<NoticeProduct> | undefined,
  productUuid: string,
): boolean | undefined => {
  const list = products ? [...products] : undefined;
  if (!serverMarksMandatoryProducts(list)) return undefined;
  return gatedProductUuids(list).has(productUuid);
};

export const noticeConfirmVerdict = <P extends ScopedPurpose>(
  config: ProductConfig & { purposes: P[] },
  selectedPurposes: Record<string, boolean>,
  recordedPurposeSelections: Record<string, boolean>,
): ConfirmVerdict =>
  confirmVerdict({
    products: config.products,
    purposes: config.purposes,
    rows: rowsOf(config),
    selectedPurposes,
    recordedPurposeSelections,
  });
